import { FormGroup } from '@angular/forms';
import {
  RfaComponents,
  RfaFormControl,
  RfaValidators,
  type RfaAppointmentControls,
} from '@innogy/feh/rfa';

import type { LeadAppointmentFormRendering } from './lead-appointment-form.interface';
import { fieldToValue } from '../utils/field-to-value';
import { LeadAppointmentFormDefaults } from './lead-appointment-form-defaults';

export function getLeadAppointmentFormControls(
  rendering: LeadAppointmentFormRendering,
  analyticsComponentName: string = rendering.componentName
): RfaAppointmentControls {
  return {
    day: new RfaFormControl(
      '',
      {
        label: fieldToValue(
          rendering.fields.DayLabel,
          LeadAppointmentFormDefaults.fields.DayLabel
        ),
        emptyValueText: fieldToValue(
          rendering.fields.DayEmptyText,
          LeadAppointmentFormDefaults.fields.DayEmptyText
        ),
        validators: [RfaValidators.required],
        entries: getAvailableDates().map((date) => ({
          text: date.toISOString(),
          value: date,
        })),
        errorMsgs: {
          required: {
            msg: fieldToValue(
              rendering.fields.DayRequiredError,
              LeadAppointmentFormDefaults.fields.DayRequiredError
            ),
            analytics: {
              componentName: analyticsComponentName,
            },
          },
        },
        analytics: {
          valueChangeTracker: {
            componentName: analyticsComponentName,
          },
        },
      },
      RfaComponents.Select
    ),
    time: new RfaFormControl(
      '',
      {
        label: fieldToValue(
          rendering.fields.TimeLabel,
          LeadAppointmentFormDefaults.fields.TimeLabel
        ),
        emptyValueText: fieldToValue(
          rendering.fields.TimeEmptyText,
          LeadAppointmentFormDefaults.fields.TimeEmptyText
        ),
        validators: [RfaValidators.required],
        entries: getAvailableTimeslots().map((timeSlot) => ({
          text: timeSlot.start.slice(0, 5),
          value: timeSlot.id,
        })),
        errorMsgs: {
          required: {
            msg: fieldToValue(
              rendering.fields.TimeRequiredError,
              LeadAppointmentFormDefaults.fields.TimeRequiredError
            ),
            analytics: {
              componentName: analyticsComponentName,
            },
          },
        },
        analytics: {
          valueChangeTracker: {
            componentName: analyticsComponentName,
          },
        },
      },
      RfaComponents.Select
    ),
  };
}

export function createLeadAppointmentForm(
  rendering: LeadAppointmentFormRendering
) {
  return new FormGroup(getLeadAppointmentFormControls(rendering));
}

function getAvailableDates(startDate = new Date(), delayWorkDays = 2): Date[] {
  const dates: Date[] = [];
  let delayCounter = 0;
  startDate = new Date(startDate); // Clone to avoid mutating the original date

  while (dates.length < 5) {
    startDate.setDate(startDate.getDate() + 1); // Move to the next day
    if (startDate.getDay() !== 6 && startDate.getDay() !== 0) {
      // Check if it's a workday
      if (delayCounter >= delayWorkDays) {
        // Check if initial delay is met
        dates.push(new Date(startDate)); // Add the date if it's a workday
      }
      delayCounter++; // Increment delay/workday counter
    }
  }

  return dates;
}

function getAvailableTimeslots() {
  return [
    '08:30',
    '09:00',
    '09:30',
    '10:00',
    '10:30',
    '13:00',
    '13:30',
    '14:00',
    '14:30',
    '15:00',
    '15:30',
    '16:00',
    '16:30',
  ].map((t: string) => ({ id: t, start: t, end: t }));
}
